import { useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import debounce from 'lodash.debounce';

/**
 * @description Not an ideal solution, but one that works without bringing any new dependencies.
 * Because we don't know the the height of the messages container, and because we don't know
 * how many images exist to load, and because browsers all have a different unspecified scroll
 * animation duration for the 'smooth' scroll behavior, we can poll the scroll position until
 * we've reached the bottom.
 * @param {{ loading: boolean, messages: Message[], selectedConversation: Conversation }} props
 * @returns {{ containerRef: React.RefObject<HTMLDivElement> }}
 */
export const useChatAutoScroll = ({
  loading,
  selectedConversation,
  messages
}) => {
  const containerRef = useRef(null);

  /** Disable the auto scroll after it has fired the first time. */
  const [didCompleteAutoScroll, setDidCompleteAutoScroll] =
    useStateIfMounted(true);

  const handleScroll = useCallback(
    node => {
      if (didCompleteAutoScroll) return;
      node.scrollTo({ top: node.scrollHeight });
    },
    [didCompleteAutoScroll]
  );

  useLayoutEffect(() => {
    const scrollPositionHasReachedBottom = node => {
      if (!node) return true;
      return node.scrollTop >= node.scrollHeight - node.clientHeight - 1;
    };

    const pollScrollPosition = (node, didCompleteAutoScroll) => {
      if (!node || didCompleteAutoScroll) return;
      handleScroll(node);
    };

    if (
      containerRef.current &&
      !loading &&
      selectedConversation &&
      !didCompleteAutoScroll
    ) {
      pollScrollPosition(containerRef.current, didCompleteAutoScroll);
    }

    if (
      containerRef.current &&
      scrollPositionHasReachedBottom(containerRef.current)
    )
      setDidCompleteAutoScroll(true);
  }, [didCompleteAutoScroll, containerRef, loading]);

  /**
   * Clear the polling interval and reset the state to auto scroll again
   * for new conversations or when new messages are added.
   */
  useEffect(() => {
    setDidCompleteAutoScroll(false);
  }, [selectedConversation, messages]);

  /**
   * Disable the auto-scroll behavior when the user scrolls.
   */
  useEffect(() => {
    const handleWheel = () => setDidCompleteAutoScroll(true);
    const removeEventListener = () =>
      containerRef?.current?.removeEventListener('mousewheel', handleWheel);

    containerRef?.current?.addEventListener('mousewheel', handleWheel);

    return removeEventListener;
  }, [containerRef, selectedConversation, loading, messages]);

  const retriggerAutoScrollOnImageLoad = debounce(
    () => setDidCompleteAutoScroll(false),
    150,
    { trailing: true }
  );

  return {
    containerRef,
    retriggerAutoScrollOnImageLoad
  };
};
